import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Flame, Sparkles, RefreshCw, Copy, Mail, Loader2 } from "lucide-react";

function ScoreBadge({ score, priority }) {
  if (score === null || score === undefined) {
    return <Badge variant="outline" className="border-dashed" data-testid="score-pending"><Sparkles className="w-3 h-3 mr-1" />scoring…</Badge>;
  }
  const cls = priority === "high"
    ? "bg-primary text-primary-foreground"
    : priority === "medium"
      ? "bg-accent/15 text-accent border-transparent"
      : "bg-muted text-muted-foreground border-transparent";
  return (
    <Badge className={`${cls} font-bold`} data-testid={`score-${priority}`}>
      {priority === "high" && <Flame className="w-3 h-3 mr-1" />}
      {score}/100
    </Badge>
  );
}

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("score");
  const [open, setOpen] = useState(null);
  const [rescoring, setRescoring] = useState(null);

  const load = async (sort = "score") => {
    try { const { data } = await api.get(`/admin/leads?sort=${sort}`); setItems(data); } catch {}
  };
  useEffect(() => { load(tab); }, [tab]);

  const rescore = async (lid) => {
    setRescoring(lid);
    try {
      await api.post(`/admin/leads/${lid}/rescore`);
      toast.success("Re-scored");
      load(tab);
      if (open?.id === lid) {
        const { data } = await api.get(`/admin/leads?sort=${tab}`);
        setOpen(data.find((x) => x.id === lid) || null);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRescoring(null); }
  };

  const updateStatus = async (lid, status) => {
    try { await api.patch(`/admin/leads/${lid}`, { status }); toast.success(`Marked ${status}`); load(tab); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyReply = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Reply copied to clipboard"); } catch { toast.error("Copy failed"); }
  };

  const openMail = (lead) => {
    const subject = encodeURIComponent(`Safeway Finland — ${lead.interest ? lead.interest[0].toUpperCase() + lead.interest.slice(1) : "Inquiry"}`);
    const body = encodeURIComponent(lead.suggested_reply || "");
    window.location.href = `mailto:${lead.email}?subject=${subject}&body=${body}`;
  };

  const totals = {
    high: items.filter((l) => l.buyer_priority === "high").length,
    medium: items.filter((l) => l.buyer_priority === "medium").length,
    low: items.filter((l) => l.buyer_priority === "low").length,
    pending: items.filter((l) => l.intent_score === null || l.intent_score === undefined).length,
  };

  return (
    <DashboardLayout title="Leads">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-primary text-primary-foreground rounded-lg p-4 card-lift" data-testid="stat-hot">
          <div className="overline opacity-80 flex items-center gap-2"><Flame className="w-4 h-4" /> Hot</div>
          <div className="font-heading text-3xl font-black mt-1">{totals.high}</div>
        </div>
        <div className="bg-white border border-border rounded-lg p-4 card-lift" data-testid="stat-warm">
          <div className="overline text-accent">Warm</div>
          <div className="font-heading text-3xl font-black text-secondary mt-1">{totals.medium}</div>
        </div>
        <div className="bg-white border border-border rounded-lg p-4 card-lift" data-testid="stat-cold">
          <div className="overline text-muted-foreground">Cold</div>
          <div className="font-heading text-3xl font-black text-secondary mt-1">{totals.low}</div>
        </div>
        <div className="bg-white border border-border rounded-lg p-4 card-lift" data-testid="stat-pending">
          <div className="overline text-muted-foreground flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Scoring</div>
          <div className="font-heading text-3xl font-black text-secondary mt-1">{totals.pending}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="lead-sort-tabs">
          <TabsTrigger value="score" data-testid="sort-score">By AI score</TabsTrigger>
          <TabsTrigger value="recent" data-testid="sort-recent">Most recent</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="mt-6">
        <CardHeader><CardTitle className="font-heading tracking-tight">{items.length} lead{items.length===1?"":"s"}</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="admin-leads-empty">No leads yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((l) => (
                <div key={l.id} className="py-4 flex items-start gap-4" data-testid={`admin-lead-${l.id}`}>
                  <div className="shrink-0 w-14 grid place-items-center pt-1">
                    <ScoreBadge score={l.intent_score} priority={l.buyer_priority} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <div className="font-heading font-bold text-secondary">{l.name}</div>
                      <a href={`mailto:${l.email}`} className="text-sm text-primary hover:underline">{l.email}</a>
                      {l.company && <span className="text-sm text-muted-foreground">· {l.company}</span>}
                      {l.country && <span className="text-sm text-muted-foreground">· {l.country}</span>}
                      <Badge variant="outline" className="capitalize text-[10px]">{l.interest || "other"}</Badge>
                      <Badge variant="outline" className="capitalize text-[10px]" data-testid={`lead-status-${l.id}`}>{l.status || "new"}</Badge>
                    </div>
                    {l.intent_summary && (
                      <div className="mt-2 text-sm text-secondary/90 italic flex items-start gap-2">
                        <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                        <span>{l.intent_summary}</span>
                      </div>
                    )}
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{l.message}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => setOpen(l)} data-testid={`open-lead-${l.id}`}>Open</Button>
                      {l.suggested_reply && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => copyReply(l.suggested_reply)} data-testid={`copy-reply-${l.id}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy reply</Button>
                          <Button size="sm" onClick={() => openMail(l)} data-testid={`email-lead-${l.id}`}><Mail className="w-3.5 h-3.5 mr-1" />Email now</Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => rescore(l.id)} disabled={rescoring === l.id} data-testid={`rescore-${l.id}`}>
                        {rescoring === l.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                        Re-score
                      </Button>
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground shrink-0">{new Date(l.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="lead-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tight flex items-center gap-3">
            {open?.name}
            {open && <ScoreBadge score={open.intent_score} priority={open.buyer_priority} />}
          </DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <a href={`mailto:${open.email}`} className="text-primary hover:underline">{open.email}</a>
                {open.company && <> · {open.company}</>}
                {open.country && <> · {open.country}</>}
              </div>
              {open.intent_summary && (
                <div className="p-4 rounded-md bg-primary/5 border border-primary/20 text-sm text-secondary flex gap-3">
                  <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="overline text-primary mb-1">AI READ</div>
                    <div>{open.intent_summary}</div>
                  </div>
                </div>
              )}
              <div>
                <div className="overline text-muted-foreground mb-1">Message from prospect</div>
                <p className="text-sm text-secondary whitespace-pre-wrap leading-relaxed">{open.message}</p>
              </div>
              {open.suggested_reply && (
                <div>
                  <div className="overline text-muted-foreground mb-1 flex items-center gap-2"><Sparkles className="w-3 h-3 text-primary"/>Suggested reply</div>
                  <div className="p-4 rounded-md bg-muted/40 border border-border text-sm text-secondary whitespace-pre-wrap leading-relaxed" data-testid="lead-suggested-reply">{open.suggested_reply}</div>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyReply(open.suggested_reply)}><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
                    <Button size="sm" onClick={() => openMail(open)}><Mail className="w-3.5 h-3.5 mr-1" />Open in email</Button>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                <div className="text-xs text-muted-foreground mr-2 self-center">Move to:</div>
                {["contacted", "qualified", "won", "lost"].map((s) => (
                  <Button key={s} size="sm" variant="outline" onClick={() => { updateStatus(open.id, s); setOpen({...open, status: s}); }} data-testid={`set-${s}`} className="capitalize">{s}</Button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => rescore(open?.id)} disabled={rescoring === open?.id}>
              {rescoring === open?.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Re-score
            </Button>
            <Button variant="outline" onClick={() => setOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
